import { useMemo } from "react";
import { Alert, Box, Button } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "../datagrid";
import {
  entityNameColumn,
  requesterColumn,
  reviewerCommentColumn,
  statusColumn,
  submittedColumn
} from "./columns";
import {
  OnboardingRequestBook,
  OnboardingRequestQuery,
  OnboardingRequestRowBase
} from "./types";

type Props<TRow extends OnboardingRequestRowBase> = {
  book: OnboardingRequestBook<TRow>;
  /**
   * The book's "my requests" query. Passed as a hook and called unconditionally below, the same way
   * LifecycleAttachmentApi passes its hooks. Each call site supplies a stable reference.
   */
  useMyRequests: () => OnboardingRequestQuery<TRow>;
};

/**
 * A requester's own onboarding requests. Identical for counterparties, brokers and countries, which
 * is why it lives here rather than three times over: only the DTO, the query and the route to the
 * approved entity differ, and those come in through the book.
 *
 * No requester column, unlike the admin grid. Every row is yours.
 */
export const MyOnboardingRequestsGrid = <
  TRow extends OnboardingRequestRowBase
>({
  book,
  useMyRequests
}: Props<TRow>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const submitted = (location.state as { submitted?: boolean } | null)
    ?.submitted;

  const { data, isLoading, isFetching } = useMyRequests();
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<GridColDef<TRow>[]>(
    () => [
      entityNameColumn(book),
      submittedColumn(),
      statusColumn(),
      reviewerCommentColumn(),
      {
        field: "actions",
        headerName: "",
        width: 160,
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const entityId = book.resultingEntityId(params.row);

          return params.row.status === "Approved" && entityId ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                navigate(book.entityPath(entityId));
              }}
            >
              Open
            </Button>
          ) : null;
        }
      }
    ],
    [book, navigate]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        minHeight: 0
      }}
    >
      {submitted && (
        <Alert severity="success">Submitted, an admin will review.</Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          loading={isLoading || isFetching}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};

// Re-exported so a book's page can build a bespoke column set on top of the shared ones without a
// second import path.
export { requesterColumn };
